import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { errorMessage, fieldErrors } from "../../../api/client";
import { adminApi } from "../../../api/resources";
import type { EmployeeDetail } from "../../../api/types";
import { Modal } from "../../../components/Overlay";
import { Button, DemoBadge, ErrorText, Field, StatusPill, TextInput } from "../../../components/ui";
import { EMPLOYEE_STATUS_LABEL, INVITATION_STATUS_LABEL, fmtDate } from "../../../utils/labels";
import { LoadState } from "../shared";
import { EmployeeAssignments } from "./EmployeeAssignments";
import { EmployeeSmsSection } from "./EmployeeSmsSection";

function Row({ label, value, testId }: { label: string; value: string; testId: string }) {
  return (
    <div className="grid gap-1 border-b border-white/5 py-3 sm:grid-cols-[180px_1fr]">
      <dt className="label-caps text-zinc-500">{label}</dt>
      <dd data-testid={testId} className="break-words text-sm text-white">{value}</dd>
    </div>
  );
}

export default function EmployeeDetailPage() {
  const { employeeId = "" } = useParams();
  const [emp, setEmp] = useState<EmployeeDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  const load = useCallback(() => {
    adminApi.employee(employeeId).then(setEmp).catch((e) => setError(errorMessage(e)));
  }, [employeeId]);
  useEffect(load, [load]);

  const reactivate = async () => {
    try {
      const res = await adminApi.reactivate(employeeId);
      setEmp(res);
      toast.success(`Reactivated as ${EMPLOYEE_STATUS_LABEL[res.account_status]}`);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <section data-testid="employee-detail-page" className="animate-rise">
      <Link to="/admin/employees" data-testid="employee-detail-back-link" className="mb-6 inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white">
        <ArrowLeft className="h-4 w-4" /> All employees
      </Link>
      <LoadState error={error} loading={!emp && !error} testId="employee-detail-error" />
      {emp && (
        <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
          <div className="rounded-sm border border-white/10 bg-surface p-8">
            <div className="flex flex-wrap items-center gap-3">
              <h1 data-testid="employee-detail-name" className="font-display text-4xl font-bold uppercase tracking-tight text-white">{emp.full_name}</h1>
              <DemoBadge show={emp.is_demo} testId="employee-detail-demo-badge" />
              <StatusPill status={emp.account_status} label={EMPLOYEE_STATUS_LABEL[emp.account_status]} testId="employee-detail-status" />
            </div>
            <dl className="mt-6">
              <Row label="Email" value={emp.email} testId="employee-detail-email" />
              <Row label="Phone" value={emp.phone_e164 ?? "—"} testId="employee-detail-phone" />
              <Row label="Phone verified" value={emp.phone_verified ? `Yes (${fmtDate(emp.phone_verified_at)})` : "No"} testId="employee-detail-phone-verified" />
              <Row label="Created" value={fmtDate(emp.created_at)} testId="employee-detail-created" />
              <Row label="Registered" value={fmtDate(emp.registered_at)} testId="employee-detail-registered" />
              <Row label="Activated" value={fmtDate(emp.activated_at)} testId="employee-detail-activated" />
              <Row label="Deactivated" value={fmtDate(emp.deactivated_at)} testId="employee-detail-deactivated" />
              {emp.deactivation_reason && <Row label="Deactivation reason" value={emp.deactivation_reason} testId="employee-detail-deactivation-reason" />}
              <Row label="Invitation" testId="employee-detail-invitation"
                value={emp.invitation ? `${INVITATION_STATUS_LABEL[emp.invitation.status]} · invited as "${emp.invitation.full_name}" on ${fmtDate(emp.invitation.created_at)}` : "No invitation on record"} />
              <Row label="SMS consent" value={emp.consent_acknowledged_at ? `${fmtDate(emp.consent_acknowledged_at)} (${emp.consent_text_version})` : "—"} testId="employee-detail-consent" />
            </dl>
          </div>
          <aside className="space-y-3">
            <Button variant="secondary" className="w-full" onClick={() => setEditing(true)} data-testid="employee-edit-button">Edit details</Button>
            {emp.account_status === "DEACTIVATED" ? (
              <Button className="w-full" onClick={reactivate} data-testid="employee-reactivate-button">Reactivate</Button>
            ) : (
              <Button variant="secondary" className="w-full hover:border-signal hover:text-signal" onClick={() => setDeactivating(true)} data-testid="employee-deactivate-button">Deactivate</Button>
            )}
            <p className="text-xs text-zinc-500">Accounts become Active only after phone verification (coming in the next milestone). There is no manual activation.</p>
          </aside>
          <EditModal emp={emp} open={editing} onClose={() => setEditing(false)} onSaved={(e) => { setEmp(e); setEditing(false); }} />
          <DeactivateModal emp={emp} open={deactivating} onClose={() => setDeactivating(false)} onDone={(e) => { setEmp(e); setDeactivating(false); }} />
        </div>
      )}
      {emp && <EmployeeSmsSection emp={emp} onChanged={() => adminApi.employee(emp.id).then(setEmp).catch(() => undefined)} />}
      {emp && <EmployeeAssignments employeeId={emp.id} />}
    </section>
  );
}

function EditModal({ emp, open, onClose, onSaved }: { emp: EmployeeDetail; open: boolean; onClose: () => void; onSaved: (e: EmployeeDetail) => void }) {
  const [form, setForm] = useState({ full_name: emp.full_name, email: emp.email, phone: emp.phone_e164 ?? "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  useEffect(() => setForm({ full_name: emp.full_name, email: emp.email, phone: emp.phone_e164 ?? "" }), [emp, open]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setFields({});
    const body = {
      ...(form.full_name !== emp.full_name && { full_name: form.full_name }),
      ...(form.email !== emp.email && { email: form.email }),
      ...(form.phone && form.phone !== emp.phone_e164 && { phone: form.phone }),
    };
    try {
      const res = await adminApi.updateEmployee(emp.id, body);
      toast.success("Employee updated");
      onSaved(res);
    } catch (err) {
      const f = fieldErrors(err);
      if (Object.keys(f).length) setFields(f);
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Edit employee" testId="employee-edit-modal">
      <form onSubmit={submit} className="space-y-5" noValidate>
        {(["full_name", "email", "phone"] as const).map((k) => (
          <Field key={k} label={{ full_name: "Full name", email: "Email", phone: "Phone" }[k]} htmlFor={`edit-${k}`}>
            <TextInput id={`edit-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`edit-${k.replace("_", "-")}-input`} />
            {fields[k] && <p data-testid={`edit-${k.replace("_", "-")}-error`} className="text-xs text-red-300">{fields[k]}</p>}
          </Field>
        ))}
        <p className="text-xs text-zinc-500">Changing the phone resets phone verification. Active employees return to "Registered – phone unverified".</p>
        <ErrorText message={error} testId="edit-error" />
        <div className="flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose} data-testid="edit-cancel-button">Cancel</Button>
          <Button type="submit" loading={busy} data-testid="edit-save-button">Save</Button>
        </div>
      </form>
    </Modal>
  );
}

function DeactivateModal({ emp, open, onClose, onDone }: { emp: EmployeeDetail; open: boolean; onClose: () => void; onDone: (e: EmployeeDetail) => void }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await adminApi.deactivate(emp.id, reason.trim());
      toast.success("Employee deactivated and signed out");
      setReason("");
      onDone(res);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open={open} onClose={onClose} title="Deactivate employee?" testId="deactivate-modal">
      <p className="mb-5 text-sm text-zinc-300">{emp.full_name} will be signed out immediately and can't sign in until reactivated. Their upcoming shifts will be flagged for attention.</p>
      <Field label="Reason (required)" htmlFor="deactivate-reason">
        <TextInput id="deactivate-reason" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="deactivate-reason-input" />
      </Field>
      <div className="mt-4"><ErrorText message={error} testId="deactivate-error" /></div>
      <div className="mt-6 flex justify-end gap-3">
        <Button variant="secondary" onClick={onClose} data-testid="deactivate-cancel-button">Cancel</Button>
        <Button loading={busy} disabled={reason.trim().length < 3} onClick={submit} data-testid="deactivate-confirm-button">Deactivate</Button>
      </div>
    </Modal>
  );
}
